/**
 * useShellContexts - the six context values the shell provides: session,
 * roster, targets, combat flow, attacker and the kill-teams.
 *
 * EACH VALUE IS BUILT EVERY RENDER and kept by `useShallowStable`: the same
 * object while every field is, a new one the moment any field changes. There
 * is no dependency list to forget a field from (2026-10-31): the hand-written
 * ones left fields out, hidden while other fields churned every render, and
 * once the shell's handlers and lists held still (plan shell-contexts) the
 * panels read stale pips and a hidden figure. So every field must keep its
 * identity while it reads the same, or the context is new every render (the
 * render budgets catch that). Length: each value is one object
 * (declarative). Extracted from DeathWatchSimulator.
 */
import { useMemo, useRef } from 'react';
import { brothersAround, brotherPositionsKey } from '../../engine/killTeam/around';
import { supportView } from '../../engine/killTeam/support';
import { actorSheetFields, rangeFields, reactionFields, rollFields, trackerFields } from './contextFields';
import useStableList from '../../hooks/useStableList';
import useShallowStable from '../../hooks/useShallowStable';
import { canonicalJson } from '../../engine/canonicalJson';

const NO_SEATS = [];
/** A seat as the context's readers see it: everything but the presence clock, which ticks on its own. */
// Canonical: Firestore moves a field it just wrote to the end of the object (usePlayerPresence `shownOf`).
const seatShown = (p) => canonicalJson({ ...p, lastSeen: null });

/** The session: rare-change identity and permission data. */
function useSessionContextValue(s) {
  const { user, session, isGm, sessionId, playerId, onlinePlayers, permissions, isLocked, actingBy } = s;
  // The seats, the same while only what ticks on its own moves (plan shell-contexts, A5): every heartbeat gave the
  // context a new value. No reader of the context reads those fields; the hooks and the bug report take the raw list.
  const connectedPlayers = useStableList(s.connectedPlayers ?? NO_SEATS, seatShown);
  const sessionContextValue = useShallowStable({
    user,
    session,
    sessionId,
    playerId,
    isGm,
    isLocked,
    permissions,
    onlinePlayers,
    connectedPlayers,
    // Who acts as whom, from the seats (v8.363.2) - the vox seeds groups from it.
    actingBy,
  });
  return sessionContextValue;
}

/** The roster: the source of truth, and the library and loadout verbs. */
function useRosterContextValue(s) {
  const {
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    targetCombatant,
    updateCombatantState,
    updateCombatantSheet,
    editCombatant,
    appendNarrative,
    activeTab,
    setActiveTab,
    handleSaveCombatantToLibrary,
    handleSaveStanding,
    activeLibrary,
    loadouts,
    deleteLoadout,
    handleSaveLoadout,
    handleApplyLoadout,
    handleSaveLoadoutToLibrary,
    armouryShelves,
    sessionId,
  } = s;
  // Roster context: the new source of truth. TargetsContext still exists
  // beside it during phase B, fed from this same roster via the legacy
  // adapter, so components see no change until phase C retires it.
  const rosterContextValue = useShallowStable({
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    targetId: activeTab,
    setTargetId: setActiveTab,
    target: targetCombatant,
    updateCombatantState,
    updateCombatantSheet,
    editCombatant,
    appendNarrative,
    onSaveCombatantToLibrary: handleSaveCombatantToLibrary,
    onSaveStanding: handleSaveStanding,
    // The sheets the roster's characters came from: what the live sheet's Changed list compares against.
    libraryCharacters: activeLibrary?.characters,
    loadouts,
    onSaveLoadout: handleSaveLoadout,
    onApplyLoadout: handleApplyLoadout,
    onDeleteLoadout: deleteLoadout,
    // The kit shelf (plan armoury, part 6): the GM's library kits, and where the others live.
    libraryKits: armouryShelves?.loadouts ?? null,
    onSaveLoadoutToLibrary: handleSaveLoadoutToLibrary,
    kitPlace: sessionId ? 'table' : 'browser',
  });
  return rosterContextValue;
}

/** The targets: the tab list, the active target, templates and target mutators. */
function useTargetsContextValue(s) {
  const {
    dragTab,
    setDragTab,
    tgtTemplates,
    showTgtMenu,
    setShowTgtMenu,
    newTgtName,
    setNewTgtName,
    activeTab,
    setActiveTab,
    renamingTab,
    setRenamingTab,
    tabCtx,
    setTabCtx,
    isLocked,
    tabs,
    currentTab,
    updateTab,
    defenderRollActions,
    defenderUnnaturalOverrides,
    onDefenderUnnaturalOverride,
    turnTracker,
    handleNewTab,
    handleDuplicateTab,
    handleCloseTab,
    handleRenameTab,
    handleTabDrop,
    handleSaveTgtTemplate,
    handleDeleteTgtTemplate,
    handleLoadTgtTemplate,
    doResetTarget,
    handleEditArmour,
    handleAddDefenderMod,
    handleRemoveDefenderMod,
    handleEditDefenderMod,
    impactsNow,
  } = s;
  // Handlers come from hooks and are stable; tabs/currentTab change often.
  const targetsContextValue = useShallowStable({
    tabs,
    activeTab,
    setActiveTab,
    currentTab,
    updateTab,
    renamingTab,
    setRenamingTab,
    dragTab,
    setDragTab,
    tabCtx,
    setTabCtx,
    handleTabDrop,
    handleNewTab,
    handleCloseTab,
    handleRenameTab,
    handleDuplicateTab,
    tgtTemplates,
    showTgtMenu,
    setShowTgtMenu,
    newTgtName,
    setNewTgtName,
    handleLoadTgtTemplate,
    handleSaveTgtTemplate,
    handleDeleteTgtTemplate,
    handleEditArmour,
    onAddDefenderMod: handleAddDefenderMod,
    onEditDefenderMod: handleEditDefenderMod,
    onRemoveDefenderMod: handleRemoveDefenderMod,
    doResetTarget,
    // Defender action-sheet rolls. Fresh callbacks are guaranteed: every
    // input that changes defenderRollActions (currentTab, addRollNarrative
    // via activeTab) also changes currentTab, which is in the dep array -
    // and defenderRollActions is listed explicitly anyway.
    ...rollFields(defenderRollActions),
    unnaturalOverrides: defenderUnnaturalOverrides,
    onUnnaturalOverride: onDefenderUnnaturalOverride,
    ...trackerFields(turnTracker, isLocked),
    // What just happened to each combatant, by id (engine/impacts): the body plates play it (plan body-map H1).
    impactsNow,
  });

  return targetsContextValue;
}

/** The combat flow: in-flight attack, defence, reroll and prompt state. */
function useCombatFlowContextValue(s) {
  const {
    roster,
    pendingAttack,
    firestoreNarrative,
    isDefencePending,
    pendingTurnEffect,
    handleTurnEffectChoice,
    pendingOverheat,
    handleOverheatChoice,
    handleRerollChoice,
    pendingReroll,
    pendingBonusAttack,
    handleBonusAttackChoice,
    handleDefenceChoice,
    handleDefenceStep,
    acceptRemainingHits,
    defenceResolving,
    defenceChain,
    reactions,
    effectivePendingDefence,
    handleRespondToReaction,
    handleAcceptAllReactions,
    handleGmForceClear,
    doClearNarrative,
    doCopyNarrative,
  } = s;
  // High-frequency during combat resolution; quiet otherwise.
  const combatFlowContextValue = useShallowStable({
    pendingAttack,
    pendingDefence: effectivePendingDefence,
    // WHO owes it, HOW LOUDLY it should read here, and HOW LONG they have.
    // The prompt can now be for a combatant this client is not looking at,
    // so the name is no longer implied by the tab you are on.
    ...reactionFields({
      reactions,
      roster,
      effectivePendingDefence,
      pendingAttack,
      onAcceptAll: handleAcceptAllReactions,
    }),
    onRespondToReaction: handleRespondToReaction,
    pendingReroll,
    handleRerollChoice,
    pendingBonusAttack,
    handleBonusAttackChoice,
    pendingTurnEffect,
    handleTurnEffectChoice,
    pendingOverheat,
    handleOverheatChoice,
    handleDefenceChoice,
    handleDefenceStep,
    acceptRemainingHits,
    defenceResolving,
    defenceChain,
    onGmForceClear: handleGmForceClear,
    isDefencePending,
    firestoreNarrative,
    doClearNarrative,
    doCopyNarrative,
  });

  return combatFlowContextValue;
}

/** The attacker: profiles, the slot grid, fire controls, toggles, odds, macros. */
function useAttackerContextValue(s) {
  const {
    selectedSlot,
    setSelectedSlot,
    setEditingSlot,
    fireMode,
    setFireMode,
    hatred,
    autoHatred,
    autoRerollDamage,
    setHatred,
    frenzied,
    setFrenzied,
    targetLocation,
    setTargetLocation,
    rangeBandChoice,
    setRangeBand,
    showAtkMenu,
    setShowAtkMenu,
    newAtkName,
    setNewAtkName,
    slotClipboard,
    setSlotClipboard,
    dragSlot,
    setDragSlot,
    actor,
    rerollDamage,
    setRerollDamage,
    ignoreCalledShot,
    setIgnoreCalledShot,
    positionsCount,
    atkProfiles,
    localCurrentProfile,
    currentSlots,
    attackCustomMods,
    weaponOverrides,
    setAttackCustomMods,
    setWeaponOverrides,
    slot,
    isMelee,
    effectiveSlot,
    attackerCharStats,
    attackerSourceCharacter,
    hasHammerBlow,
    attackToggles,
    setAttackToggles,
    engageCalledShot,
    clearCalledShot,
    calledShotReduction,
    hasBerserkCharge,
    attackMod,
    liveMap,
    actorTarget,
    rangeBand,
    rollActions,
    attackerUnnaturalOverrides,
    onAttackerUnnaturalOverride,
    resetActionEconomy,
    actionEconomy,
    ledgerData,
    defensiveStanceActive,
    handleToggleDefensiveStance,
    toggleStance,
    squadDrum,
    pressSquad,
    actionDrumOpen,
    setActionDrumOpen,
    attackTriggerLabel,
    actionDrumProps,
    handleAttackOrChambered,
    handleDuplicateSlot,
    handleCutSlot,
    handleCopySlot,
    handlePasteSlot,
    handleDeleteSlot,
    handleMoveSlot,
    handleMoveSlotBy,
    handleLoadAtkProfile,
    handleNewAtkProfile,
    handleSaveAtkProfile,
    handleDeleteAtkProfile,
    handleReload,
    actorModHandlers,
    handleActorToggleFrenzied,
    handleActorSetStandingReaction,
    handleActorTargetChange,
    handleActorEditArmour,
    handleActorReorderWeapons,
    fireModes,
    currentMacros,
    handleRequestMacroName,
    handleSaveMacro,
    handleLoadMacro,
    handleDeleteMacro,
    probability,
    firingSolutions,
  } = s;
  // probability, macros. Handlers are hook-stable; state changes medium-rate.
  const attackerContextValue = useShallowStable({
    atkProfiles,
    localCurrentProfile,
    showAtkMenu,
    setShowAtkMenu,
    newAtkName,
    setNewAtkName,
    handleLoadAtkProfile,
    handleSaveAtkProfile,
    handleNewAtkProfile,
    handleDeleteAtkProfile,
    currentSlots,
    selectedSlot,
    setSelectedSlot,
    setEditingSlot,
    slotClipboard,
    setSlotClipboard,
    dragSlot,
    setDragSlot,
    handleDuplicateSlot,
    handleCopySlot,
    handleCutSlot,
    handlePasteSlot,
    handleDeleteSlot,
    handleMoveSlot,
    handleMoveSlotBy,
    slot,
    effectiveSlot,
    isMelee,
    fireMode,
    setFireMode,
    fireModes,
    attackMod,
    attackToggles,
    setAttackToggles,
    attackCustomMods,
    setAttackCustomMods,
    weaponOverrides,
    setWeaponOverrides,
    hatred,
    autoHatred,
    autoRerollDamage,
    setHatred,
    frenzied,
    setFrenzied,
    rerollDamage,
    setRerollDamage,
    ignoreCalledShot,
    setIgnoreCalledShot,
    positionsCount,
    targetLocation,
    setTargetLocation,
    engageCalledShot,
    clearCalledShot,
    ...rangeFields({ rangeBand, rangeBandChoice, liveMap, setRangeBand }),
    // The trigger fires the CHAMBER (v8.236.0) - consumers keep the
    // old name, the routing lives here.
    handleAttack: handleAttackOrChambered,
    handleReload,
    actionDrumOpen,
    setActionDrumOpen,
    actionDrumProps,
    attackTriggerLabel,
    actionEconomy,
    onResetActionEconomy: resetActionEconomy,
    defensiveStanceActive,
    onToggleDefensiveStance: handleToggleDefensiveStance,
    onToggleStance: toggleStance,
    // A brother's Squad Mode run and its press, for the strip (plan modes-rework: the drum's own source).
    squadDrum,
    pressSquad,
    ledgerData,
    attackerCharStats,
    attackerSourceCharacter,
    calledShotReduction,
    hasBerserkCharge,
    hasHammerBlow,
    probabilityResult: probability.result,
    probabilityLoading: probability.loading,
    firingSolutions: firingSolutions.result,
    firingSolutionsLoading: firingSolutions.loading,
    macros: currentMacros,
    onSaveMacro: handleSaveMacro,
    onLoadMacro: handleLoadMacro,
    onDeleteMacro: handleDeleteMacro,
    onRequestMacroName: handleRequestMacroName,
    ...rollFields(rollActions),
    unnaturalOverrides: attackerUnnaturalOverrides,
    onUnnaturalOverride: onAttackerUnnaturalOverride,
    // The acting character's OWN sheet view (v7.1.0). Same projection
    // the target panel renders, pointed at whoever you are acting as -
    // which is what makes "one unified view per character" literal
    // rather than two panels that happen to describe the same person.
    ...actorSheetFields({
      actor,
      actorTarget,
      actorModHandlers,
      edits: {
        onActorToggleFrenzied: handleActorToggleFrenzied,
        onActorSetStandingReaction: handleActorSetStandingReaction,
        onActorTargetChange: handleActorTargetChange,
        onActorEditArmour: handleActorEditArmour,
        onActorReorderWeapons: handleActorReorderWeapons,
      },
    }),
  });
  return attackerContextValue;
}

/**
 * The kill-teams (Squad Mode): their documents, the table's verbs, his brothers around him for a plate's B-scope, and
 * his support ring and threads for the map - both from the RULE markers (positions off: no map, as every rule sees it,
 * so nothing contradicts D22's "a distance the map cannot give is in range"). `around` and `support` change identity
 * when a brother moves and only then, so the B-scope and the threads follow his move and a stranger's move re-renders
 * no plate (the stage 8 review). One identity while the documents, the verbs and the brothers' places hold.
 */
function useKillTeamsContextValue(s) {
  const { killTeams, killTeamActions } = s;
  const live = useRef(s);
  live.current = s;
  const roster = s.sharedRoster ?? s.roster ?? [];
  const where = brotherPositionsKey(roster, s.ruleTokens ?? {}, Object.keys(killTeams?.teams.killTeams ?? {}));
  // A new function only when `where` changes (the roster and markers are read live through the ref).
  const made = useRef({ where: null, around: null });
  if (made.current.where !== where) {
    made.current = {
      where,
      around: (id) =>
        brothersAround(live.current.sharedRoster ?? live.current.roster ?? [], live.current.ruleTokens ?? {}, id),
      support: (actorId, shared, preview) =>
        live.current.killTeams
          ? supportView(actorId, shared, live.current.killTeams.teams, live.current.ruleTokens ?? {}, preview)
          : null,
    };
  }
  const { around, support } = made.current;
  return useMemo(
    () => (killTeams ? { ...killTeams, actions: killTeamActions, around, support } : null),
    [killTeams, killTeamActions, around, support],
  );
}

/**
 * @param {object} s - the shell scope, complete (every domain has run).
 * @returns {{sessionContextValue: object, rosterContextValue: object, killTeamsContextValue: object | null,
 *   targetsContextValue: object, combatFlowContextValue: object,
 *   attackerContextValue: object}}
 */
export default function useShellContexts(s) {
  const sessionContextValue = useSessionContextValue(s);
  const rosterContextValue = useRosterContextValue(s);
  const targetsContextValue = useTargetsContextValue(s);
  const combatFlowContextValue = useCombatFlowContextValue(s);
  const attackerContextValue = useAttackerContextValue(s);
  const killTeamsContextValue = useKillTeamsContextValue(s);
  return {
    sessionContextValue,
    rosterContextValue,
    targetsContextValue,
    combatFlowContextValue,
    attackerContextValue,
    killTeamsContextValue,
  };
}
